import { LegAnsokanFormFieldName, LegAnsokanField, Yrke } from '../LegAnsokanForm.types';
import {
  SkyddadYrkestitelFormStepOneModel,
  SkyddadYrkestitelAnsokan,
  AnsokanFormTexts,
  UtbildningsForm,
  Utbildning,
} from '../../../models/skyddadyrkestitel.model';
import { FormFieldFile } from 'sos-common/js/commonTypes';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import { ValidationObject } from 'sos-common/js/commonTypes/validation.types';
import { SelectBox, SelectBoxOption } from 'sos-common/js';
import { isDateValid } from 'sos-common/js/utils/date';
import { LEG_FILE_MIMETYPES } from '../../FilKomplettering/FilKomplettering.mimetypes';
import { validateUtbildningstid, validateCentury } from '../../../services/yrkestitelansokanform.validation.service';
import { MultiSelectItem } from '../Specialistbevis/SpecialistbevisAnsokanForm.types';
export class SkyddadYrkestitelFieldMap {
  private _map: Map<LegAnsokanFormFieldName | string, BaseField>;
  public allFieldsAreValid: boolean = false;
  private _emailNoMatch: string;
  constructor(
    model: SkyddadYrkestitelFormStepOneModel,
    ansokan: SkyddadYrkestitelAnsokan,
    formTexts: AnsokanFormTexts,
    educatedInSweden: boolean,
    translations: (key: string, defaultValue?: string) => string,
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    addMultiSelectItem: (selectedItems: MultiSelectItem[]) => void
  ) {
    const map = new Map();
    this._map = map;
    this._emailNoMatch = translations('errorMessages/emailNoMatch');
    map.set(
      LegAnsokanFormFieldName.Email,
      new EpostField(LegAnsokanFormFieldName.Email, ansokan?.Epost, true, translations)
    );
    map.set(
      LegAnsokanFormFieldName.ConfirmEmail,
      new EpostField(LegAnsokanFormFieldName.ConfirmEmail, ansokan?.Epost, false, translations)
    );
    if (educatedInSweden) {
      map.set(
        LegAnsokanFormFieldName.EducationForm,
        new EducationForm(
          LegAnsokanFormFieldName.EducationForm,
          model?.Utbildningsform,
          ansokan.Utbildning.UtbildningsformId,
          false,
          translations
        )
      );

      map.set(
        LegAnsokanFormFieldName.EducationPeriod,
        new EducationPeriod(
          LegAnsokanFormFieldName.EducationPeriod,
          ansokan.Utbildning.UtbildningsperiodId,
          educatedInSweden,
          ansokan.Utbildning,
          model.Utbildningsform,
          translations,
          LegAnsokanFormFieldName.EducationPeriod.toString()
        )
      );
    } else {
      map.set(
        LegAnsokanFormFieldName.EducationTitle,
        new EducationTitle(
          LegAnsokanFormFieldName.EducationTitle,
          ansokan?.Utbildning?.Utbildningstitel,
          !educatedInSweden,
          translations
        )
      );

      map.set(
        LegAnsokanFormFieldName.EducationLength,
        new EducationLength(
          LegAnsokanFormFieldName.EducationLength,
          ansokan?.Utbildning?.Utbildningslangd,
          !educatedInSweden,
          translations
        )
      );
      map.set(
        LegAnsokanFormFieldName.UtbildningsLand,
        new UtbildningsLand(
          LegAnsokanFormFieldName.UtbildningsLand,
          model?.UtbildningsLander,
          ansokan.Utbildning.UtbildningslandId,
          !educatedInSweden,
          translations
        )
      );
      map.set(
        LegAnsokanFormFieldName.ExaminationYear,
        new ExaminationYearField(
          LegAnsokanFormFieldName.ExaminationYear,
          ansokan?.Utbildning.Examensar,
          translations,
          !educatedInSweden
        )
      );
      map.set(
        LegAnsokanFormFieldName.DateOfCertificate,
        new DateOfCertificate(
          LegAnsokanFormFieldName.DateOfCertificate,
          ansokan?.Utbildning.DatumForIntygOmYrkesbehorighet,
          translations,
          !educatedInSweden
        )
      );
      map.set(
        LegAnsokanFormFieldName.OtherCertCountries,
        new OtherCertCountry(
          LegAnsokanFormFieldName.OtherCertCountries,
          model?.UtbildningsLander,
          ansokan.Utbildning.LanderForBehorighetsBevis,
          !educatedInSweden,
          translations,
          ansokan.Utbildning.BehorighetsbevisFranAnnatLand === true,
          addMultiSelectItem
        )
      );
    }

    if (model.FileUploadText1 !== null) {
      const fileupload1FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload1,
        model.FileUploadText1?.Label,
        ansokan?.Fileupload1Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText1.Required,
        educatedInSweden ? model.FileUploadText1.ShowSv : model.FileUploadText1.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload1, fileupload1FieldState);
    }

    if (model.FileUploadText2 !== null) {
      const fileupload2FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload2,
        model.FileUploadText2?.Label,
        ansokan?.Fileupload2Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText2.Required,
        educatedInSweden ? model.FileUploadText2.ShowSv : model.FileUploadText2.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload2, fileupload2FieldState);
    }

    if (model.FileUploadText3 !== null) {
      const fileupload3FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload3,
        model.FileUploadText3?.Label,
        ansokan?.Fileupload3Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText3.Required,
        educatedInSweden ? model.FileUploadText3.ShowSv : model.FileUploadText3.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload3, fileupload3FieldState);
    }

    if (model.FileUploadText4 !== null) {
      const fileupload4FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload4,
        model.FileUploadText4?.Label,
        ansokan?.Fileupload4Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText4.Required,
        educatedInSweden ? model.FileUploadText4.ShowSv : model.FileUploadText4.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload4, fileupload4FieldState);
    }
    if (model.FileUploadText5 !== null) {
      const fileupload5FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload5,
        model.FileUploadText5?.Label,
        ansokan?.Fileupload5Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText5.Required,
        educatedInSweden ? model.FileUploadText5.ShowSv : model.FileUploadText5.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload5, fileupload5FieldState);
    }
    if (model.FileUploadText6 !== null) {
      const fileupload6FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload6,
        model.FileUploadText6?.Label,
        ansokan?.Fileupload6Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText6.Required,
        educatedInSweden ? model.FileUploadText6.ShowSv : model.FileUploadText6.ShowOutsideOfSv
      );
      map.set(LegAnsokanFormFieldName.FileUpload6, fileupload6FieldState);
    }
    map.set(
      LegAnsokanFormFieldName.TermsAndConditions,
      new TermsAndConditionsField(LegAnsokanFormFieldName.TermsAndConditions, formTexts.SamtyckeLabel, translations)
    );
  }

  toggleShowFileFor = (fieldName: LegAnsokanFormFieldName) => {
    const filesField = this._map.get(fieldName);
    if (!filesField) return;
    filesField.ShowField = !filesField.ShowField;
  };

  showFieldFor = (fieldName: LegAnsokanFormFieldName): boolean => {
    const field = this._map.get(fieldName);
    return !!field && field.ShowField;
  };

  addOnChangeEventHandler = (inputChanged, onChangeTermsAndCondition) => {
    this._map.forEach((base) => {
      base.props.onChange =
        base.props.fieldName === LegAnsokanFormFieldName.TermsAndConditions ? onChangeTermsAndCondition : inputChanged;
    });
  };

  get = (fieldName: LegAnsokanFormFieldName | string): BaseField => this._map.get(fieldName);

  clone = () => {
    this._map = new Map(this._map);
    return this;
  };

  removeAllNonExistingFields = () => {
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload1);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload2);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload3);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload4);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload5);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload6);
  };

  private _removeFilesWithNullValueId = (fileFieldName: LegAnsokanFormFieldName) => {
    const field = this._map.get(fileFieldName);
    if (!field) return;
    const fileField = field.props;
    fileField.value = fileField.value.filter((file) => !!file.FileId);
  };

  /**
   * Does not actually deletes the file but just clears the state, sort of.
   */
  removeFilesByTag = (
    tag:
      | LegAnsokanFormFieldName.FileUpload1
      | LegAnsokanFormFieldName.FileUpload2
      | LegAnsokanFormFieldName.FileUpload3
      | LegAnsokanFormFieldName.FileUpload4
  ) => {
    const field = this._map.get(tag);
    if (!field) return;
    const fileField = field.props;
    fileField.value = [];
  };

  setFileValueByFieldName = (field: FormFieldFile) => {
    const fieldInMap = this._map.get(field.fieldName);
    if (!fieldInMap) return;
    const fileField = fieldInMap.props;
    fileField.value = field.value;
  };

  setFileErrorMessage = (message: string, tag: string) => {
    var examinationFileState = this._map.get(tag);
    if (examinationFileState.props.value.length <= 1 && examinationFileState.props.isRequired) {
      examinationFileState.props.errorMessage = message;
    } else {
      examinationFileState.props.infoMessage = message;
    }
  };

  setErrorMessage = (message: string, tag: string) => {
    var fieldState = this._map.get(tag);
    if (message) {
      fieldState.props.errorMessage = message;
    }
  };

  validateAllFields() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const result = field.validate();
      if (!result.isValid) {
        this.allFieldsAreValid = false;
        return;
      }
    }
    const emailsAreSame = this.validateEmailComparison();
    this.allFieldsAreValid = emailsAreSame;
  }

  validateAllFieldsWithErrorMessages() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const isValid = this.validateFieldWithErrorMessage(field);
      if (!isValid) {
        this.allFieldsAreValid = false;
      }
    }
  }

  validateFieldWithErrorMessage = (field: BaseField): boolean => {
    let fieldIsValid = field.validateWithErrorMessage();
    let emailsAreSame = field.shouldValidateUsingOtherFields ? this.validateEmailComparison() : true;

    const isValid = fieldIsValid && emailsAreSame;
    if (!isValid) this.allFieldsAreValid = false;
    return isValid;
  };

  validateEmailComparison = (): boolean => {
    const emailField = this._map.get(LegAnsokanFormFieldName.Email);
    const confirmEmailField = this._map.get(LegAnsokanFormFieldName.ConfirmEmail);

    const areDifferent = confirmEmailField.props.value && emailField.props.value !== confirmEmailField.props.value;
    confirmEmailField.props.errorMessage = areDifferent ? this._emailNoMatch : '';
    return !areDifferent;
  };
}

class BaseField {
  protected _props;
  public ShowField;
  protected _Translations: (key: string, defaultValue?: string) => string;
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = true,
    showField: boolean = true
  ) {
    this._props = {
      fieldName: fieldName,
      isRequired: mandatory,
      pattern: null,
      patternMessage: '',
      value: null, //NOTE: Value have different types in subtypes.
      errorMessage: '',
      infoMessage: '', //Everything should not be handled as an error but more like information
      actAsNumber: false,
      maxLength: null,
      showMaxLengthMessage: false,
      checkboxWithProps: false,
    };
    this._Translations = translations;
    this.ShowField = showField;
  }

  get props(): LegAnsokanField {
    return this._props;
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,

      errorMessage: '',
    };

    result.isValid = this.validateExists();
    if (!result.isValid) {
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    result.isValid = this.validatePattern();
    if (!result.isValid) {
      result.errorMessage = props.patternMessage;
      return result;
    }
    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  protected validateExists = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    return props.isRequired && valueExists;
  };

  protected validatePattern = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    if (!props.pattern || !valueExists) return true;
    return new RegExp(props.pattern).test(props.value);
  };

  get shouldValidateUsingOtherFields() {
    return false;
  }
}

class OtherCertCountry extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    countries: SelectBoxOption[],
    value: string[],
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string,
    isRequired?: boolean,
    addMultiSelectItem?: (selectedItems: MultiSelectItem[]) => void
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.items = this.mapCountriesToMultiSelectItems(countries, value) as MultiSelectItem[];
    props.defaultOptionText = translations('defaultOtherCountryText');
    props.isRequired = isRequired;
    props.value = value ?? [];
    props.showAllOptionText = false;
    props.enableShowAllOption = false;
    props.textBoxLabelText = `${translations('ifHasOtherCountryText')} ${translations('mandatory')}`;
    props.showRemoveSelectionsButton = false;
    props.onSelectedItemsChange = addMultiSelectItem;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;
    if (props.isRequired) {
      if (props.isRequired) {
        if (props.value.length < 1) {
          result.isValid = false;
          result.errorMessage = this._Translations('errorMessages/required');
          return result;
        }
      }
    }

    if (props.value.length > 5) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/onlyFiveCountries');
      return result;
    }
    return result;
  };

  mapCountriesToMultiSelectItems = (
    countries: SelectBoxOption[],
    CurrentlySavedValues: string[]
  ): MultiSelectItem[] => {
    return countries.map((country) => ({
      key: country.key,
      value: country.text,
      isSelectedByDefault: CurrentlySavedValues.includes(country.key),
    }));
  };
}
class EducationTitle extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('educationName')} ${translations('mandatory')}`;
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    let maxLength = 255;
    if (props.value.length > maxLength) {
      result.isValid = false;
      result.errorMessage = this._Translations('toBigString');
      return result;
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class EducationForm extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    utbildningsFormer: UtbildningsForm[],
    utbildningsFormId: string,
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations, showField);

    const selectBoxUtbildningsForm = utbildningsFormer.map((utbildningsform) => {
      return {
        key: utbildningsform.Utbildningsform.Id,
        text: utbildningsform.Utbildningsform.Namn,
      } as SelectBoxOption;
    });
    const props = this._props;
    props.label = `${translations('educationForm')} ${translations('mandatory')}`;
    props.defaultOptionText = 'Välj';
    props.isRequired = true;
    props.className = 'm-b-2';
    props.options = selectBoxUtbildningsForm;
    props.value = utbildningsFormId ?? '';
  }
}

class EducationPeriod extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    utbildning: Utbildning,
    utbildningsformModel: UtbildningsForm[],
    translations: (key: string, defaultValue?: string) => string,
    ariaLabel?: string
  ) {
    var concatanatedValue = value !== null ? utbildning.UtbildningsformId + '_' + value : null;
    super(fieldName, translations, showField);
    const props = this._props;
    props.isRequired = this.isRequired(utbildning, utbildningsformModel); //Check if it should be required on page load/component rerender
    props.label = `${translations('studyPeriodQuestion')} ${translations('mandatory')}`;
    props.value = value ?? null;
    props.checkboxWithProps = true;
    props.ariaLabel = ariaLabel;
    props.subLabel = translations('studyPeriodSubLabel');
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (props.isRequired && (props.value === null || props.value.length === 0)) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    return result;
  };

  private isRequired = (utbildning: Utbildning, utbildningsformModel: UtbildningsForm[]): boolean => {
    return utbildning.UtbildningsformId !== '' && this.hasUtbildningsPerioder(utbildning, utbildningsformModel);
  };

  //If there is no utbildningsperioder on the utbildningsform, the field does not have to be required
  private hasUtbildningsPerioder = (utbildning: Utbildning, utbildningsformModel: UtbildningsForm[]): boolean => {
    if (utbildning.UtbildningsformId === '' || utbildning.UtbildningsformId === null) return false;

    return (
      utbildningsformModel.filter((x) => x.Utbildningsform.Id === utbildning.UtbildningsformId)[0].Utbildningsperiod
        .length > 0
    );
  };
}

class UtbildningsLand extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    countries: SelectBoxOption[],
    EuCountry: string,
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    (props.label = `${translations('educationCountry')} ${translations('mandatory')}`),
      (props.defaultOptionText = translations('defaultOptionText'));
    props.className = 'm-b-2';
    props.options = countries;
    props.value = EuCountry ?? '';
  }
}

class EducationLength extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('educationLength')} ${translations('mandatory')}`;
    props.isRequired = true;
    props.value = value ?? '';
  }
  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    const validationError = validateUtbildningstid(props.value, 99, this._Translations);
    if (validationError != '') {
      result.isValid = false;
      result.errorMessage = validationError;
      return result;
    }
    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };
}

class ExaminationYearField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
    showField: boolean = true
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('graduationYear')} ${translations('mandatory')}`;
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }
    const error = validateCentury(props.value, this._Translations);

    if (error != '') {
      result.isValid = false;
      result.errorMessage = error;
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class EpostField extends BaseField {
  protected _show: boolean = true;
  private _shouldValidate: boolean;
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    shouldValidate: boolean = true,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.id = fieldName;
    props.autoComplete = 'email';
    props.label = `${translations('email')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.pattern =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zåäöA-ZÅÄÖ\-0-9]+\.)+[a-zåäöA-ZÅÄÖ]{2,}))$/;
    props.patternMessage = translations('errorMessages/emailWrongFormat');
    this._shouldValidate = shouldValidate;
  }

  get shouldValidateUsingOtherFields() {
    return true;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!this._shouldValidate) return result;

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    let maxLength = 255;
    if (props.value.length > maxLength) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/toBigString');
      return result;
    }

    if (!this.validatePattern()) {
      result.isValid = false;
      result.errorMessage = props.patternMessage;
      return result;
    }

    const disallowedChars = ['å', 'Å', 'ä', 'Ä', 'ö', 'Ö'];
    for (let index = 0; index < disallowedChars.length; index++) {
      const char = disallowedChars[index];
      if (props.value.includes(char)) {
        result.isValid = false;
        result.errorMessage = this._Translations('errorMessages/disallowedChars');
        return result;
      }
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class DateOfCertificate extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    examensDatum: string,
    translations: (key: string, defaultValue?: string) => string,
    showField: boolean = true
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.value = examensDatum ?? '';
    props.label = `${translations('licenseFrom')}`;
    props.id = fieldName;
  }

  validate = (): ValidationObject => {
    const props = this._props;

    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (props.value == '') return result;

    //validate format
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(props.value)) {
      (result.errorMessage = this._Translations('errorMessages/correctDateFormat')), (result.isValid = false);
      return result;
    }

    //validate date
    const valueSplit = props.value.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      (result.errorMessage = this._Translations('errorMessages/correctDateError')), (result.isValid = false);
      return result;
    }

    //can't be higher than today
    if (Date.parse(props.value) > Date.now()) {
      (result.errorMessage = this._Translations('errorMessages/FutureDateError')), (result.isValid = false);
      return result;
    }

    // var temp = valueSplit[0];
    var enteredYear = parseInt(valueSplit[0], 10);
    if (enteredYear < 1900) {
      //Borde egentligen konfigureras och ett mer korrekt äldsta datum sättas. Men detta fångar de mest uppenbara felfallen.
      (result.errorMessage = this._Translations('errorMessages/oldDateError')), (result.isValid = false);
      return result;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class FilesField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    label: string,
    value: FileExtendedDetails[],
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = false,
    showField: boolean = true
  ) {
    super(fieldName, translations, mandatory, showField);
    const props = this._props;
    props.heading = label ?? '';
    props.max = 10;
    props.showMaxFilesInfoMessage = true;
    props.disabled = false;
    props.nofilesText = translations('noFileUploaded');
    props.onAdd = onAdd;
    props.onRemove = onRemove;
    props.errorMessage = null;
    props.value = value;
    props.mimetype = LEG_FILE_MIMETYPES;
    props.upload = translations('uploadFileText');
    props.maxFileSizeErrorMessage = translations('maxFileSizeErrorMessage');
    props.maxFilesInfoMessage = translations('maxFilesInfoMessage');
    props.illegalFileNameErrorMessage = translations('illegalFileNameErrorMessage');
    props.removeText = translations('removeText');
    props.removeFileText = translations('removeFileText');
    props.removeFilePopupBody = translations('removeFilePopupBody');
    props.abortText = translations('abortText');
    props.closeText = translations('closeText');
  }

  validate = (): ValidationObject => {
    let result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (props.isRequired && !this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  validateExists = () => {
    const props = this._props;
    return props.value.length > 0;
  };
}
class TermsAndConditionsField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    label: string,
    translations: (key: string, defaultValue?: string) => string
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.label = label;
    props.value = false;
    //props.autoComplete = 'off';
  }

  validate = (): ValidationObject => ({
    isValid: this.props.value,
    errorMessage: '',
  });
  validateWithErrorMessage = (): boolean => {
    const result = this.validate();

    this.props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}
